import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from './AppLayout';
import { buttonStyle, cardTitle, inputStyle, labelStyle, subtitle } from './quoteStyles';

const addonOptions = [
  { value: 'roadside_assist', label: 'Roadside Assistance' },
  { value: 'accidental_damage', label: 'Accidental Damage Cover' },
  { value: 'contents_cover', label: 'Contents Cover' },
];

const CustomerCoverageScreen = () => {
  const navigate = useNavigate();
  const { applicationId } = useParams();

  const [form, setForm] = useState({
    sumInsured: 500000,
    paymentFrequency: 'monthly',
    addons: [],
  });
  const [error, setError] = useState('');

  useEffect(() => {
    const loadApplication = async () => {
      const response = await fetch(`/api/application/${applicationId}`);
      if (!response.ok) {
        navigate('/quote/start');
        return;
      }

      const data = await response.json();
      if (data.coverage) {
        setForm({
          sumInsured: data.coverage.sumInsured || 500000,
          paymentFrequency: data.coverage.paymentFrequency || 'monthly',
          addons: Array.isArray(data.coverage.addons) ? data.coverage.addons : [],
        });
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const toggleAddon = addon => {
    const selected = (form.addons || []).includes(addon);
    setForm({
      ...form,
      addons: selected
        ? (form.addons || []).filter(item => item !== addon)
        : [...(form.addons || []), addon],
    });
  };

  const validate = () => {
    const sum = Number(form.sumInsured);
    if (Number.isNaN(sum) || sum < 50000 || sum > 5000000) {
      return 'Sum insured must be between 50,000 and 5,000,000.';
    }
    if (!['monthly', 'annual'].includes(form.paymentFrequency)) {
      return 'Please choose a valid payment frequency.';
    }
    return '';
  };

  const saveAndContinue = async event => {
    event.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const response = await fetch(`/api/application/${applicationId}/coverage`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sumInsured: Number(form.sumInsured),
        paymentFrequency: form.paymentFrequency,
        addons: form.addons || [],
      }),
    });

    const data = await response.json().catch(() => ({ error: 'Unexpected server response' }));
    if (!response.ok) {
      setError(data.error || 'Unable to save coverage details');
      return;
    }

    navigate(`/quote/${applicationId}/review`);
  };

  return (
    <AppLayout>
      <h1 style={cardTitle}>Coverage Preferences</h1>
      <p style={subtitle}>Step 3 of 4: Choose your coverage details.</p>
      {error && <div style={styles.errorBanner}>{error}</div>}

      <form onSubmit={saveAndContinue} style={styles.grid}>
        <div>
          <label style={labelStyle}>Sum insured</label>
          <input
            type="number"
            min="50000"
            step="1000"
            required
            value={form.sumInsured}
            onChange={event => setForm({ ...form, sumInsured: event.target.value })}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Payment frequency</label>
          <select
            required
            value={form.paymentFrequency}
            onChange={event => setForm({ ...form, paymentFrequency: event.target.value })}
            style={inputStyle}
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Optional add-ons</label>
          <div style={styles.addonRow}>
            {addonOptions.map(addon => (
              <label key={addon.value} style={styles.addonLabel}>
                <input
                  type="checkbox"
                  checked={form.addons.includes(addon.value)}
                  onChange={() => toggleAddon(addon.value)}
                />
                <span>{addon.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={styles.actions}>
          <button
            type="button"
            style={styles.backButton}
            onClick={() => navigate(`/quote/${applicationId}/customer`)}
          >
            Back
          </button>
          <button type="submit" style={buttonStyle}>
            Save and Continue
          </button>
        </div>
      </form>
    </AppLayout>
  );
};

const styles = {
  grid: {
    display: 'grid',
    gap: 14,
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  },
  addonRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 14,
  },
  addonLabel: {
    alignItems: 'center',
    background: '#f5fbf7',
    border: '1px solid #d8e9df',
    borderRadius: 8,
    color: '#2f634a',
    display: 'flex',
    gap: 8,
    padding: '9px 10px',
  },
  actions: {
    alignItems: 'flex-end',
    display: 'flex',
    gap: 10,
  },
  backButton: {
    background: '#eff7f2',
    border: '1px solid #bdd8c7',
    borderRadius: 8,
    color: '#236946',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 700,
    padding: '11px 18px',
  },
  errorBanner: {
    background: '#fff5f5',
    border: '1px solid #f3b8b8',
    borderRadius: 8,
    color: '#9e2e2e',
    marginBottom: 12,
    padding: '10px 12px',
  },
};

export default CustomerCoverageScreen;
